
import {
    BulleAide,
    FieldBirthplaceSelect,
    FieldCountrySelect,
    FieldInput,
    FieldPatternInput,
    FieldRadioGroup,
    Question,
    RgpdInfoBloc,
    SmartButton,
} from '@workspace/core-ts/components';
import { useModal } from '@workspace/core-ts/contexts';
import { SmartFormGlobal, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { useGlobalForm } from '../../contexts/global-form-context';
import { useAvenant, useSource } from '@workspace/core-ts/hooks';
import { Permis } from '@workspace/core-ts/icons-amo';
import { CIVILITE_OPTIONS, isAdult, isAfter1900, isNotInTheFuture } from '@workspace/utils';
import DOMPurify from 'dompurify';
import { useEffect } from 'react';
import { informationsContent, useContent } from '../../content';
import type { StepContentConfig } from '../../utils';

interface InformationsStepProps {
    content?: StepContentConfig;
    showHeaderIcon?: boolean;
    headerIcon?: React.ComponentType<any>;
    children?: React.ReactNode;
    showAllFamily?: boolean;
    disableFilledFieldsOnAvenant?: boolean;
    /** Liste des champs qui restent éditables même en mode avenant (ex: ['Civilite', 'BirthName']) */
    editableFieldsOnAvenant?: string[];
    /** Afficher le bloc RGPD sur les données personnelles (défaut: true, affiché si source site + April Moto) */
    showRgpdBlock?: boolean;
}

export function InformationsStep({
    content = informationsContent,
    showHeaderIcon = true,
    headerIcon: HeaderIcon = Permis,
    children,
    showAllFamily = false,
    disableFilledFieldsOnAvenant = false,
    editableFieldsOnAvenant = [],
    showRgpdBlock = true,
}: InformationsStepProps) {
    const methods = useSmartFormGlobal();
    const r = useContent();
    const isAvenant = useAvenant();
    const { isDirect, isAprilMoto } = useSource();
    const { showModal } = useModal();
    const { config } = useGlobalForm();

    const codeVendeurMAE = methods.watch('DemandeTarif.Police.CodeVendeurMAE');

    const isFieldDisabled = (fieldName: string): boolean => {
        if (!disableFilledFieldsOnAvenant || !isAvenant) return false;

        // Extraire le nom du champ (dernière partie du path, ex: "Civilite" depuis "DemandeTarif.ListePersonnes.0.Civilite")
        const fieldShortName = fieldName.split('.').pop() || '';

        // Si le champ est dans la liste des champs éditables, ne pas le désactiver
        if (editableFieldsOnAvenant.includes(fieldShortName)) {
            return false;
        }

        const value = methods.getValues(fieldName);
        return value !== undefined && value !== null && value !== '';
    };

    useEffect(() => {
        methods.setValue('DemandeTarif.ListePersonnes.0.TypePersonne', 'P');
        methods.setValue('DemandeTarif.ListePersonnes.0.RoleConducteur', 'P');
        methods.setValue('DemandeTarif.ListePersonnes.0.Souscripteur', true);
    }, [methods]);

    return (
        <SmartFormGlobal {...methods} className="mx-auto">
            <Question.Header>
                {showHeaderIcon && HeaderIcon && <HeaderIcon />}
                <Question.Title>{r(content.title)}</Question.Title>
            </Question.Header>

            {isDirect && isAprilMoto && showRgpdBlock && (
                <BulleAide title={r(content.rgpdTitle)}>
                    <p>{r(content.rgpdDescription)}</p>
                    <button
                        type="button"
                        onClick={() => showModal({
                            title: r(content.rgpdTitle),
                            children: (
                                <div
                                    className="text-left space-y-3 text-sm"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r(content.rgpdExpandedContent) as string) }}
                                />
                            ),
                            className: 'max-w-2xl',
                        })}
                        className="text-primary hover:underline mt-2 text-sm font-medium"
                    >
                        En savoir plus
                    </button>
                </BulleAide>
            )}

            {showAllFamily && (
                <BulleAide>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r(content.allFamilly) as string) }} />
                </BulleAide>
            )}

            <FieldRadioGroup
                label={r(content.civiliteLabel)}
                name="DemandeTarif.ListePersonnes[0].Civilite"
                options={CIVILITE_OPTIONS}
                type="small"
                disabled={isFieldDisabled('DemandeTarif.ListePersonnes.0.Civilite')}
                validation={{
                    required: 'La civilité est requise',
                }}
            />

            <FieldInput
                label={r(content.nomLabel)}
                name="DemandeTarif.ListePersonnes[0].Nom"
                type="text"
                autoComplete="family-name"
                placeholder={r(content.nomPlaceholder)}
                disabled={isFieldDisabled('DemandeTarif.ListePersonnes.0.Nom')}
                normalize="uppercaseStrict"
                validation={{
                    required: 'Votre nom de famille est requis',
                }}
            />

            <FieldInput
                label={r(content.nomNaissanceLabel)}
                name="DemandeTarif.ListePersonnes[0].BirthName"
                type="text"
                autoComplete="family-name"
                placeholder={r(content.nomNaissancePlaceholder)}
                disabled={isFieldDisabled('DemandeTarif.ListePersonnes.0.BirthName')}
                normalize="uppercaseStrict"
                validation={{
                    required: 'Votre nom de naissance est requis',
                }}
            />

            <FieldInput
                label={r(content.prenomLabel)}
                name="DemandeTarif.ListePersonnes[0].Prenom"
                type="text"
                autoComplete="given-name"
                placeholder={r(content.prenomPlaceholder)}
                disabled={isFieldDisabled('DemandeTarif.ListePersonnes.0.Prenom')}
                normalize="uppercaseStrict"
                validation={{
                    required: 'Le prénom est requis',
                    minLength: {
                        value: 2,
                        message: 'Le prénom doit contenir au moins 2 caractères',
                    },
                }}
            />

            <FieldPatternInput
                label={r(content.dateNaissanceLabel)}
                name="DemandeTarif.ListePersonnes[0].DateNaissance"
                type="date"
                disabled={isFieldDisabled('DemandeTarif.ListePersonnes.0.DateNaissance')}
                validation={{
                    required: 'La date de naissance est requise',
                    validate: {
                        isNotInTheFuture,
                        isAdult,
                        isAfter1900,
                    },
                }}
            />

            <FieldCountrySelect
                name="DemandeTarif.ListePersonnes[0].BirthCountry"
                nameBirthCity="DemandeTarif.ListePersonnes[0].BirthCity"
                nameBirthDepartment="DemandeTarif.ListePersonnes[0].BirthDepartment"
                disabled={isFieldDisabled('DemandeTarif.ListePersonnes.0.BirthCountry')}
            />

            <FieldBirthplaceSelect
                nameBirthplace="DemandeTarif.ListePersonnes[0].BirthCity"
                nameCountry="DemandeTarif.ListePersonnes[0].BirthCountry"
                nameDepartment="DemandeTarif.ListePersonnes[0].BirthDepartment"
                disabled={isFieldDisabled('DemandeTarif.ListePersonnes.0.BirthCity')}
            />

            {codeVendeurMAE && <BulleAide description={r(content.codeVendeurMAEInfo)} />}

            {children}

            <RgpdInfoBloc letterUrl={config?.rgpd?.letterUrl} controllerName={config?.rgpd?.controllerName} />

            <SmartButton />
        </SmartFormGlobal>
    );
}
